/**
 * Picks the chain from `/chains.json` before anything loads: the last choice,
 * else the hostname's first label (base.aile.finance), else the first entry.
 * Switching reloads the page: sessions, orders and the wallet's network belong
 * to one backend.
 */

import { storageKeys, timing } from "../config";

export interface NativeCurrency {
  name: string;
  symbol: string;
  decimals: number;
}

export interface ChainEntry {
  /** "base", "bsc": also the proxy route and the host label. */
  key: string;
  /** Label for the switcher. */
  label: string;
  /** API prefix for this chain's gateway, e.g. "/api/base". */
  apiBase: string;
  /**
   * The network facts a wallet needs to add the chain. Optional: an older
   * `chains.json` lacks them, and then the features that need them refuse.
   */
  chainId?: number;
  nativeCurrency?: NativeCurrency;
  explorer?: { name: string; url: string } | null;
  walletRpcUrls?: string[];
}

const DEFAULT_ENTRY: ChainEntry = { key: "default", label: "", apiBase: "/api" };

let chains: ChainEntry[] = [DEFAULT_ENTRY];
let active: ChainEntry = DEFAULT_ENTRY;

function readStored(): string | null {
  try {
    return localStorage.getItem(storageKeys.chain);
  } catch {
    return null;
  }
}

function hostLabel(): string {
  try {
    return window.location.hostname.split(".")[0]?.toLowerCase() ?? "";
  } catch {
    return "";
  }
}

export function pickChain(list: ChainEntry[], stored: string | null, host: string): ChainEntry {
  const byKey = (k: string | null) => (k === null ? undefined : list.find((c) => c.key === k));
  return byKey(stored) ?? byKey(host) ?? list[0] ?? DEFAULT_ENTRY;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const isText = (value: unknown): value is string => typeof value === "string" && value !== "";

function readNativeCurrency(value: unknown): NativeCurrency | undefined {
  if (!isRecord(value)) return undefined;
  const { name, symbol, decimals } = value;
  if (!isText(name) || !isText(symbol)) return undefined;
  if (typeof decimals !== "number" || !Number.isInteger(decimals) || decimals < 0) return undefined;
  return { name, symbol, decimals };
}

function readExplorer(value: unknown): ChainEntry["explorer"] {
  if (value === null) return null;
  if (!isRecord(value) || !isText(value.name) || !isText(value.url)) return undefined;
  return { name: value.name, url: value.url };
}

/**
 * One `chains.json` entry, or null without a key and an API prefix. A network
 * fact of the wrong shape is dropped, so what needs it refuses rather than
 * handing a wallet a malformed request.
 */
export function readChainEntry(raw: unknown): ChainEntry | null {
  if (!isRecord(raw) || typeof raw.key !== "string" || typeof raw.apiBase !== "string") return null;
  const entry: ChainEntry = {
    key: raw.key,
    label: isText(raw.label) ? raw.label : raw.key,
    apiBase: raw.apiBase,
  };
  const { chainId, walletRpcUrls } = raw;
  if (typeof chainId === "number" && Number.isSafeInteger(chainId) && chainId > 0) {
    entry.chainId = chainId;
  }
  const nativeCurrency = readNativeCurrency(raw.nativeCurrency);
  if (nativeCurrency !== undefined) entry.nativeCurrency = nativeCurrency;
  const explorer = readExplorer(raw.explorer);
  if (explorer !== undefined) entry.explorer = explorer;
  if (Array.isArray(walletRpcUrls) && walletRpcUrls.every(isText)) {
    entry.walletRpcUrls = [...walletRpcUrls];
  }
  return entry;
}

/** Loads `/chains.json` and selects the active chain. Never rejects. */
export async function loadChains(): Promise<ChainEntry> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error("Network directory did not answer"));
    }, timing.wallet.walletConnectReadMs);
  });
  try {
    const read = async (): Promise<ChainEntry[]> => {
      const res = await fetch("/chains.json", { headers: { accept: "application/json" }, signal: controller.signal });
      if (!res.ok) return [];
      const parsed: unknown = await res.json();
      return Array.isArray(parsed) ? parsed.flatMap((entry) => readChainEntry(entry) ?? []) : [];
    };
    const valid = await Promise.race([read(), deadline]);
    if (valid.length > 0) chains = valid;
  } catch {
    // A single-chain site: keep the implicit default.
  } finally {
    clearTimeout(timer);
  }
  active = pickChain(chains, readStored(), hostLabel());
  return active;
}

export function getChains(): ChainEntry[] {
  return chains;
}

export function getActiveChain(): ChainEntry {
  return active;
}

/** The directory's entry for a chain id, the selected one first. */
export function chainEntryFor(chainId: number): ChainEntry | undefined {
  if (active.chainId === chainId) return active;
  return chains.find((c) => c.chainId === chainId);
}

/** The prefix every `/api/...` request is rewritten to. */
export function getApiBase(): string {
  return active.apiBase;
}

/** Remembers the choice and reloads: another backend is a fresh session. */
export function selectChain(key: string): void {
  if (key === active.key) return;
  try {
    localStorage.setItem(storageKeys.chain, key);
  } catch {
    // Private mode: the reload still lands on the host/default pick.
  }
  window.location.reload();
}

/** Test hook. */
export function setChainsForTest(list: ChainEntry[], activeKey?: string): void {
  chains = list;
  active = pickChain(list, activeKey ?? null, "");
}
